import { Component, Input, OnInit } from '@angular/core'
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms'
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap'
import { ToastService } from '@northfork/ng-basics'
import { Observable } from 'rxjs'
import { map } from 'rxjs/operators'
import {
  CorporateAction,
  CorporateActionModal,
} from '../models/corporate-action'
import { CorporateActionInputEdit } from '../models/corporate-action-input-edit'
import { CorporateActionType } from '../models/corporate-action-type'
import { SecurityGroupInput } from '../models/security-group-input'
import { CorporateActionsService } from '../services/corporate-actions.service'
import { parseBulkEntry, parseCusips } from '../util/corporate-actions.util'
import {
  asyncValidateIf,
  validateIf,
} from '../validators/conditional-validators'
import {
  cusipValidator,
  jiraStatusValidationMessages,
  securityValidationMessages,
  verifyJiraStatusAsyncValidator,
} from '../validators/corporate-action-validator'
import { SecurityService } from './../services/security.service'
import { BulkEntryDialogComponent } from './bulk-entry-dialog/bulk-entry-dialog.component'
import { CorporateActionSaveConfirmationModal } from './corporate-actions-save-confirmation-modal'

@Component({
  selector: 'hs-corporate-actions-create-edit',
  templateUrl: './corporate-actions-create-edit.component.html',
  styleUrls: ['./corporate-actions-create-edit.component.scss'],
})
export class CorporateActionsCreateEditComponent implements OnInit {
  constructor(
    public activeModal: NgbActiveModal,
    public corporateActionsService: CorporateActionsService,
    public securityService: SecurityService,
    public modalService: NgbModal,
    public formBuilder: FormBuilder,
    public toastService: ToastService,
  ) {}

  USE_JIRA_STATUS = 'USE_JIRA_STATUS'
  corporateActionForm: FormGroup = new FormGroup({})
  types: CorporateActionType[] = []
  corporateActionDetailsLoading = false
  corporateAction: CorporateAction | undefined
  jiraStatusValidationMessage = jiraStatusValidationMessages
  securityValidationMessage = securityValidationMessages
  forceReflag: FormControl = new FormControl(false)
  title = 'Add Corporate Action'

  @Input() corporateActionInput?: CorporateActionModal

  ngOnInit(): void {
    this.createCorporateActionForm()
    this.corporateActionsService
      .getCorporateActionTypes()
      .subscribe(types => (this.types = types))

    if (this.corporateActionInput?.corporateActionId) {
      this.title = 'Edit Corporate Action'
      this.corporateActionDetailsLoading = true
      this.corporateActionsService
        .searchById(this.corporateActionInput?.corporateActionId, [])
        .subscribe(
          corporateAction => {
            this.corporateAction = corporateAction
            this.editCorporateActionForm(corporateAction)
            this.initializeSecurityGroupForms(this.corporateAction)

            this.corporateActionDetailsLoading = false
          },
          () => {
            this.corporateActionDetailsLoading = false
          },
        )
    } else {
      this.corporateActionForm.markAllAsTouched()
      this.securityGroupForms.push(this.createSecurityGroupForm([], []))
    }

    this.corporateActionForm?.controls.manualStatus?.valueChanges.subscribe(
      () => {
        setTimeout(() =>
          this.corporateActionForm.controls.jiraIssueKey.updateValueAndValidity(),
        )
      },
    )
  }

  isEdit() {
    return this.corporateActionInput?.corporateActionId !== undefined
  }

  createCorporateActionForm() {
    this.corporateActionForm = this.formBuilder.group({
      type: ['', Validators.required],
      mandatory: ['mandatory', Validators.required],
      effectiveDate: [''],
      jiraIssueKey: [
        '',
        {
          validators: validateIf(
            () => this.isManualStatusUseJiraStatus(),
            Validators.required,
          ),
          asyncValidators: asyncValidateIf(
            () => this.isManualStatusUseJiraStatus(),
            verifyJiraStatusAsyncValidator(this.corporateActionsService),
          ),
          updateOn: 'blur',
        },
      ],
      notes: [undefined],
      securityGroupForms: this.formBuilder.array([], Validators.required),
      manualStatus: [this.USE_JIRA_STATUS, Validators.required],
    })
  }

  editCorporateActionForm(corporateAction: CorporateAction) {
    this.corporateActionForm = this.formBuilder.group({
      type: [corporateAction.type, Validators.required],
      mandatory: [
        corporateAction.mandatory == null
          ? 'unknown'
          : corporateAction.mandatory
          ? 'mandatory'
          : 'voluntary',
        Validators.required,
      ],
      effectiveDate: [corporateAction.effectiveDate],
      jiraIssueKey: [
        corporateAction.jiraIssueKey,
        {
          validators: validateIf(
            () => this.isManualStatusUseJiraStatus(),
            Validators.required,
          ),
          asyncValidators: asyncValidateIf(
            () => this.isManualStatusUseJiraStatus(),
            verifyJiraStatusAsyncValidator(this.corporateActionsService),
          ),
          updateOn: 'blur',
        },
      ],
      notes: [corporateAction.notes],
      securityGroupForms: this.formBuilder.array([], Validators.required),
      manualStatus: [
        corporateAction.manualStatus
          ? corporateAction.manualStatus
          : this.USE_JIRA_STATUS,
        Validators.required,
      ],
    })
    this.corporateActionForm.markAllAsTouched()
  }

  /**
   * Initialize security input forms from an existing corporate action.
   */
  initializeSecurityGroupForms(data: CorporateAction) {
    // Build a map of security ids to CUSIPs using security info from the model
    const cusipsById: Map<number, string> = new Map<number, string>()
    data.securityGroups.forEach(securityGroup => {
      securityGroup.parentSecurities.forEach(security => {
        cusipsById.set(security.securityId, security.cusip)
      })

      if (securityGroup.childSecurities) {
        securityGroup.childSecurities.forEach(security => {
          cusipsById.set(security.securityId, security.cusip)
        })
      }
    })

    // Create parents/children input forms from existing data
    data.securityGroupInputs.forEach(input => {
      const parentCusips =
        input.parentSecurityIds.length != 0
          ? input.parentSecurityIds.map(id => cusipsById.get(id))
          : []
      const childCusips = input.childSecurityIds
        ? input.childSecurityIds.map(id => cusipsById.get(id))
        : []
      this.securityGroupForms.push(
        this.createSecurityGroupForm(parentCusips, childCusips),
      )
    })
  }

  isManualStatusUseJiraStatus(): boolean {
    return (
      this.corporateActionForm &&
      this.corporateActionForm.value.manualStatus === this.USE_JIRA_STATUS
    )
  }

  get securityGroupForms(): FormArray {
    return <FormArray>this.corporateActionForm.controls['securityGroupForms']
  }

  addSecurityGroup() {
    this.securityGroupForms.push(this.createSecurityGroupForm([], []))
  }

  deleteSecurityGroup(index: number) {
    this.securityGroupForms.removeAt(index)
  }

  createSecurityGroupForm(
    parentCusips: (string | undefined)[],
    childCusips: (string | undefined)[],
  ): FormGroup {
    const securityGroupForm = this.formBuilder.group({
      parentCusips: [
        parentCusips,
        {
          validators: Validators.required,
          asyncValidators: cusipValidator(this.securityService),
          updateOn: 'blur',
        },
      ],
      childCusips: [
        childCusips,
        {
          asyncValidators: cusipValidator(this.securityService),
          updateOn: 'blur',
        },
      ],
    })
    securityGroupForm.markAllAsTouched()
    this.onCusipChange(<FormControl>securityGroupForm.controls['parentCusips'])
    this.onCusipChange(<FormControl>securityGroupForm.controls['childCusips'])
    return securityGroupForm
  }

  onCusipChange(cusipsForm: FormControl): void {
    cusipsForm.valueChanges.subscribe(value => {
      if (typeof value === 'string') {
        cusipsForm.setValue(parseCusips(value), { emitEvent: false })
      }
    })
  }

  openSaveModal() {
    if (this.forceReflag.value) {
      const modalRef = this.modalService.open(
        CorporateActionSaveConfirmationModal,
        { keyboard: false },
      )
      modalRef.result.then((confirmed: boolean) => {
        if (confirmed) {
          this.saveCorporateAction()
        }
      })
    } else {
      this.saveCorporateAction()
    }
  }

  openBulkModal() {
    const modalRef = this.modalService.open(BulkEntryDialogComponent, {
      keyboard: false,
    })
    modalRef.result.then((bulkEntry: string) => {
      if (bulkEntry) {
        this.bulkEnterSecurityGroups(parseBulkEntry(bulkEntry))
      }
    })
  }

  /**
   * Create security groups from the output of the bulk entry tool.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  bulkEnterSecurityGroups(inputs: any[]): void {
    while (this.securityGroupForms.length !== 0) {
      this.securityGroupForms.removeAt(0)
    }

    // Create and populate new security group forms
    inputs.forEach(input => {
      const form = this.createSecurityGroupForm(
        input.parentCusips,
        input.childCusips,
      )
      form.controls['parentCusips'].markAsDirty()
      form.controls['childCusips'].markAsDirty()
      form.controls['parentCusips'].updateValueAndValidity()
      form.controls['childCusips'].updateValueAndValidity()
      this.securityGroupForms.push(form)
    })
  }
  saveCorporateAction() {
    this.corporateActionDetailsLoading = true
    this.createCorporateAction().subscribe(corporateAction => {
      if (this.corporateAction?.id) {
        this.corporateActionsService
          .editCorporateAction(corporateAction, this.corporateAction?.id)
          .subscribe(
            () => {
              this.toastService.success('Corporate Action successfully edited.')
              this.corporateActionDetailsLoading = false
              this.activeModal.close(this.corporateAction?.id)
            },
            () => {
              this.toastService.error('Corporate Action edit failed.')
              this.corporateActionDetailsLoading = false
            },
          )
      } else {
        this.corporateActionsService
          .addCorporateAction(corporateAction)
          .subscribe(
            () => {
              this.toastService.success(
                'Corporate Action successfully submitted.',
              )
              this.corporateActionDetailsLoading = false
              this.activeModal.close('ADD')
            },
            () => {
              this.toastService.error('Corporate Action add failed.')
              this.corporateActionDetailsLoading = false
            },
          )
      }
    })
  }

  createCorporateAction(): Observable<CorporateActionInputEdit> {
    // Gather all input CUSIPs, so we can map them to security IDs
    const allCusips: Set<string> = new Set<string>()

    this.corporateActionForm.value.securityGroupForms.forEach(
      (group: { parentCusips: string[]; childCusips: string[] }) => {
        group.parentCusips.forEach((cusip: string) => allCusips.add(cusip))
        group.childCusips.forEach((cusip: string) => allCusips.add(cusip))
      },
    )

    // Get the map of CUSIPs to security IDs, and construct a CorporateActionInputEdit using it
    return this.securityService.getSecurityIdsBulk(Array.from(allCusips)).pipe(
      map(idsByCusip => {
        // Create security group input objects with security IDs instead of CUSIPs
        const securityGroupInputs: SecurityGroupInput[] = []
        this.corporateActionForm.value.securityGroupForms.forEach(
          (group: {
            parentCusips: {
              map: (arg: (cusip: string) => number | undefined) => number[]
            }
            childCusips: {
              map: (arg: (cusip: string) => number | undefined) => number[]
            }
          }) => {
            const parentIds: number[] = group.parentCusips.map(
              (cusip: string) => idsByCusip.get(cusip),
            )
            const childIds: number[] = group.childCusips.map((cusip: string) =>
              idsByCusip.get(cusip),
            )

            const securityGroupInput: SecurityGroupInput = {
              parentSecurityIds: parentIds,
              childSecurityIds: childIds,
            }
            securityGroupInputs.push(securityGroupInput)
          },
        )

        const corporateAction: CorporateActionInputEdit = {
          // Use security inputs converted to security IDs
          securityGroupInputs: securityGroupInputs,

          forceReflag: this.forceReflag.value,
          type: this.corporateActionForm.value.type,
          mandatory:
            this.corporateActionForm.value.mandatory === 'mandatory'
              ? true
              : this.corporateActionForm.value.mandatory === 'voluntary'
              ? false
              : null,
          effectiveDate: this.corporateActionForm.value.effectiveDate,
          jiraIssueKey: this.corporateActionForm.value.jiraIssueKey,
          notes: this.corporateActionForm.value.notes,
          manualStatus:
            this.corporateActionForm.value.manualStatus === this.USE_JIRA_STATUS
              ? null
              : this.corporateActionForm.value.manualStatus,
        }
        return corporateAction
      }),
    )
  }
}
